/**
 * gpu_recovery — what to do when the WebGL context is lost (XR P0.3, 2026-09-28). Pure.
 *
 * WHY A RELOAD. three's WebGPURenderer on its WebGL backend never draws again after `webglcontextrestored`
 * (three.js #34682: `_isDeviceLost` is never cleared and the backend has no restore handler; the only working
 * recovery is a new renderer on a new canvas). Every buffer, texture and pipeline Atlas built is gone with the
 * context, so the honest recovery is the one the network module already uses for a dropped socket: record where
 * the player stands (lib/session_resume, ≤ 2 min) and RELOAD — the boot resumes into the same world and spot.
 * Standalone headsets lose the context under memory pressure (Quest's OS reclaims GPU memory from the browser).
 *
 * In VR the session dies with the page; a Quest-class browser may grant the reloaded page a new immersive session
 * without a click (WebXR navigation, `sessiongranted` — immersive-web/navigation). `XR_RESUME_KEY` marks that the
 * player was presenting so the boot can take a granted session, or offer "Re-enter VR".
 *
 * A context that keeps dying must not reload for ever: at most `RELOADS_MAX` reloads in `RELOAD_WINDOW_MS`, then
 * the page stays put with a message.
 */

export const XR_RESUME_KEY = 'atlas:xr-resume';
export const XR_RESUME_MAX_AGE_MS = 120_000;
export const RELOAD_LOG_KEY = 'atlas:gpu-reloads';
export const RELOADS_MAX = 3;
export const RELOAD_WINDOW_MS = 10 * 60_000;
/** A moment for the lost-context message to paint and the XR session to end cleanly. */
export const RELOAD_DELAY_MS = 1500;

export interface Store { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void }

function storeOr(s?: Store | null): Store | null {
  if (s !== undefined) return s;
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null; } catch { return null; }
}

/** The reload times still inside the window (oldest first). Never throws. */
export function recentReloads(now: number, store?: Store | null): number[] {
  const st = storeOr(store);
  if (!st) return [];
  try {
    const raw = JSON.parse(st.getItem(RELOAD_LOG_KEY) ?? '[]') as unknown;
    if (!Array.isArray(raw)) return [];
    return raw.filter((t): t is number => typeof t === 'number' && Number.isFinite(t) && t <= now + 10_000 && now - t < RELOAD_WINDOW_MS);
  } catch { return []; }
}

export interface RecoveryPlan {
  /** Reload the page (after `RELOAD_DELAY_MS`). False = too many reloads already: stay and say so. */
  reload: boolean;
  /** End the immersive session first (the page is going, and a dead frame loop in a headset is the worst case). */
  endSession: boolean;
  /** Mark that the player was in VR, so the reloaded page can take a granted session. */
  markXr: boolean;
  message: string;
}

/** Decide what a context loss does now. */
export function recoveryPlan(presenting: boolean, now: number, store?: Store | null): RecoveryPlan {
  const reloads = recentReloads(now, store);
  const reload = reloads.length < RELOADS_MAX;
  return {
    reload,
    endSession: presenting,
    markXr: presenting && reload,
    message: reload
      ? 'The graphics device was reset — reloading back to where you stand…'
      : `The graphics device keeps resetting (${reloads.length} times in ten minutes). Close other tabs or apps, then reload.`,
  };
}

/** Record a reload about to happen (call right before `location.reload()`). */
export function noteReload(now: number, store?: Store | null): void {
  const st = storeOr(store);
  if (!st) return;
  try { st.setItem(RELOAD_LOG_KEY, JSON.stringify([...recentReloads(now, st), now])); } catch { /* storage blocked */ }
}

export function markXrResume(now: number, store?: Store | null): void {
  const st = storeOr(store);
  if (!st) return;
  try { st.setItem(XR_RESUME_KEY, String(now)); } catch { /* storage blocked */ }
}

/** True when the player was presenting on a page that reloaded within the window. Consumes the mark. */
export function takeXrResume(now: number, store?: Store | null): boolean {
  const st = storeOr(store);
  if (!st) return false;
  try {
    const at = Number(st.getItem(XR_RESUME_KEY));
    st.removeItem(XR_RESUME_KEY);
    return Number.isFinite(at) && at > 0 && now - at >= -10_000 && now - at < XR_RESUME_MAX_AGE_MS;
  } catch { return false; }
}
